import { Component, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Observable, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';

import { HomerRegisterVm } from 'app/shared/generated/Models/Public/HomerRegisterVm';
import { HomerRegisterService } from './homer-register.service';
import { pcgSettings } from 'app/shared/generated/pcg-settings';
import { FormatHelperService } from 'app/shared/helpers/format-helper.service';
import { RecaptchaService } from 'app/services/recaptcha.service';

@Component({
    selector: 'pcg-homer-register',
    templateUrl: './homer-register.component.html',
    styleUrls: ['./homer-register.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HomerRegisterComponent {

    formGroup = HomerRegisterVm.Form;
	model = HomerRegisterVm.GetModel(this.formGroup);
	model$: Observable<HomerRegisterVm>;

    isSuccess = false;
    alreadyExists = false;
    isError = false;
    fromAccounts = signal<boolean>(false);
    fromIcq = signal<boolean>(false);
    fromRxRefillRequest = signal<boolean>(false);

    showGenericMsg = computed(() => 
        !this.fromAccounts() && 
        !this.fromIcq() && 
        !this.fromRxRefillRequest()
    );
    
    errorString = "";
    userGuid = "";
    loginUrl = "";

    isSubmitting = signal<boolean>(false);

    constructor(
        public router: Router
        , private route: ActivatedRoute
        , private homerRegisterService: HomerRegisterService
        , private formatHelperService : FormatHelperService
        , private recaptchaService : RecaptchaService
        , title: Title
    ) { title.setTitle('HOMER Registration'); }

    ngOnInit() {
        this.setEnv(); 
        this.setUpHoneyPot();
        
        this.route.queryParams.subscribe((params) => {	
            if (!this.formatHelperService.GetIsNullyOrWhitespace(params?.guid)) { 
                this.isError = false; 
                this.userGuid = params.guid;
            } else { 
                this.isError = true; 
                this.errorString = "There was an error retrieving your PFS user information.";
            }
            if (!this.formatHelperService.GetIsNullyOrWhitespace(params?.fromAccounts)) {
                this.fromAccounts.set(true);
            }
            if (!this.formatHelperService.GetIsNullyOrWhitespace(params?.fromIcq)) {
                this.fromIcq.set(true);
            }
            if (!this.formatHelperService.GetIsNullyOrWhitespace(params?.fromRxRefillRequest)) {
                this.fromRxRefillRequest.set(true);
            }
		});

        this.model$ = this.homerRegisterService.getUser(this.userGuid)
            .pipe(tap((vm: HomerRegisterVm) => { 
                this.formGroup.setValue(vm); 
                if (vm.alreadyExists === true) {
                    this.isError = true;
                    this.errorString = "This user and/or email already exists. " + 
                    "Please login to Homer below to proceed or contact a PFS/HOMER administrator for further assistance.";
                }
                if (vm.errorMessage !== "") { 
                    this.isError = true; 
                    this.errorString = vm.errorMessage;
                }
                this.formGroup.controls['fromAccounts'].setValue(this.fromAccounts());
                this.formGroup.controls['fromIcq'].setValue(this.fromIcq());
                this.formGroup.controls['fromRxRefillRequest'].setValue(this.fromRxRefillRequest());
            }));
    }

    setEnv() {
        if (pcgSettings.mode === 'Development') { this.loginUrl = "https://localhost:44327/"; } 
        else if (pcgSettings.mode === 'Demo') { this.loginUrl = "https://demo.fdepcs.com/login"; } 
        else { this.loginUrl = "https://fdepcs.com/login"; }
	}

    onSubmit() {
        this.isSuccess = false;
        this.isError = false;
        this.errorString = "";

        this.recaptchaService.submit(
            {
                action: 'registerHomerUser',
                apiEndpoint: 'api/PublicHomerRegister/RegisterHomerUser',
                formGroup: this.formGroup,
                loadingSignal: this.isSubmitting,
                showDefaultErrors: false // Errors are handled by this component
            },
            {
                onSuccess: (msg) => {
                    if (msg.isSuccessful) { 
                        this.isSuccess = true; 
                    } else { 
                        this.isSuccess = false; 
                        this.isError = true;
                        this.errorString = msg.message;
                    }
                },
                onCreateTokenError: () => {
                    this.isError = true;
                    this.errorString = "reCAPTCHA token generation failed. Please refresh the page and try again.";
                },
                onVerifyTokenError: (error) => {
                    this.isError = true;
                    this.errorString = error?.error || "An error occurred while submitting the form.";
                }
            }
        );
    }

    setUpHoneyPot() {
        this.formGroup.controls['honeyPot'].setValidators(this.requireFalse());
		this.formGroup.updateValueAndValidity();
    }

    requireFalse() : ValidatorFn {
		return (control : AbstractControl) : ValidationErrors | null => {
			if (control.value === true) { return { honeyPot: true}; }
			return null;
		}
	}
}
